// src/helpers/infografia/infografiaService.js
import { get, post, patch, del } from '../apiHelper';

const BASE_URL = 'infografias';

const infografiaService = {
    /**
     * Obtener lista de infografías
     * @param {string} marcaId - ID de la marca
     * @param {object} filters - Filtros opcionales (esBorrador, template, page, limit)
     * @returns {Promise<object>}
     */
    getInfografias: async (marcaId, filters = {}) => {
        const params = new URLSearchParams({
            marcaId,
            ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== undefined && v !== null))
        });

        const response = await get(`${BASE_URL}?${params.toString()}`);
        return response?.success ? response : { success: true, infografias: response || [] };
    },

    /**
     * Obtener infografía por ID
     * @param {string} id - ID de la infografía
     * @returns {Promise<object>}
     */
    getInfografia: async (id) => {
        const response = await get(`${BASE_URL}/${id}`);
        return response?.success ? response : { success: true, infografia: response };
    },

    /**
     * Crear nueva infografía
     * @param {object} infografiaData - Datos de la infografía
     * @returns {Promise<object>}
     */
    createInfografia: async (infografiaData) => {
        const response = await post(BASE_URL, infografiaData);
        return response?.success ? response : { success: true, infografia: response };
    },

    /**
     * Actualizar infografía existente
     * @param {string} id - ID de la infografía
     * @param {object} updates - Campos a actualizar
     * @returns {Promise<object>}
     */
    updateInfografia: async (id, updates) => {
        const response = await patch(`${BASE_URL}/${id}`, updates);
        return response?.success ? response : { success: true, infografia: response };
    },

    /**
     * Eliminar infografía
     * @param {string} id - ID de la infografía
     * @returns {Promise<object>}
     */
    deleteInfografia: async (id) => {
        const response = await del(`${BASE_URL}/${id}`);
        return response;
    },

    /**
     * Duplicar infografía
     * @param {string} id - ID de la infografía a duplicar
     * @returns {Promise<object>}
     */
    duplicateInfografia: async (id) => {
        const response = await post(`${BASE_URL}/${id}/duplicar`);
        return response;
    },

    /**
     * Subir imagen para elemento de infografía
     * @param {string} id - ID de la infografía
     * @param {File} imagen - Archivo de imagen
     * @param {string} elementoId - ID del elemento (opcional)
     * @returns {Promise<object>}
     */
    uploadImagen: async (id, imagen, elementoId = null) => {
        const formData = new FormData();
        formData.append('imagen', imagen);
        if (elementoId) {
            formData.append('elementoId', elementoId);
        }

        const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:3000/api';

        let token = null;
        try {
            const storedData = localStorage.getItem('auth-storage-petitprime');
            if (storedData) {
                const parsedData = JSON.parse(storedData);
                token = parsedData?.state?.token;
            }
        } catch (error) {
            console.warn('Error accessing localStorage:', error);
        }

        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${baseUrl}/${BASE_URL}/${id}/imagen`, {
            method: 'POST',
            headers,
            body: formData
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Error al subir imagen');
        }

        return await response.json();
    },

    /**
     * Subir imagen (método alternativo con FormData completo)
     * @param {string} marcaId - ID de la marca
     * @param {FormData} formData - FormData con imagen y marcaId
     * @returns {Promise<object>}
     */
    uploadImage: async (marcaId, formData) => {
        const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:3000/api';

        let token = null;
        try {
            const storedData = localStorage.getItem('auth-storage-petitprime');
            if (storedData) {
                const parsedData = JSON.parse(storedData);
                token = parsedData?.state?.token;
            }
        } catch (error) {
            console.warn('Error accessing localStorage:', error);
        }

        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${baseUrl}/${BASE_URL}/imagen`, {
            method: 'POST',
            headers,
            body: formData
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Error al subir imagen');
        }

        return await response.json();
    },

    /**
     * Registrar exportación de infografía
     * @param {string} id - ID de la infografía
     * @param {string} formato - Formato de exportación (png, pdf)
     * @param {string} calidad - Calidad (baja, media, alta)
     * @returns {Promise<object>}
     */
    registrarExportacion: async (id, formato, calidad = 'alta') => {
        const response = await post(`${BASE_URL}/${id}/exportar`, {
            formato,
            calidad
        });
        return response;
    },

    /**
     * Obtener lista de imágenes subidas por el usuario
     * @param {string} marcaId - ID de la marca (opcional)
     * @param {object} filters - Filtros opcionales (page, limit, search)
     * @returns {Promise<object>}
     */
    getImagenesUsuario: async (marcaId = null, filters = {}) => {
        const params = new URLSearchParams();
        if (marcaId) params.append('marcaId', marcaId);
        if (filters.page) params.append('page', filters.page);
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.search) params.append('search', filters.search);

        const endpoint = params.toString()
            ? `${BASE_URL}/imagenes?${params.toString()}`
            : `${BASE_URL}/imagenes`;

        const response = await get(endpoint);
        return response?.success ? response : { success: true, imagenes: response?.imagenes || response || [], pagination: response?.pagination };
    },

    // ==========================================
    // NUEVOS MÉTODOS - TEMPLATES (v1.1.0)
    // ==========================================

    /**
     * Obtener lista de templates
     * @param {object} filters - Filtros (categoria, esPublico, marcaId, tag, ordenarPorPopularidad, page, limit)
     * @returns {Promise<object>}
     */
    getTemplates: async (filters = {}) => {
        const params = new URLSearchParams(
            Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== undefined && v !== null))
        );

        const endpoint = params.toString()
            ? `${BASE_URL}/templates?${params.toString()}`
            : `${BASE_URL}/templates`;

        const response = await get(endpoint);
        return response?.success ? response : { success: true, templates: response?.templates || [], pagination: response?.pagination };
    },

    /**
     * Obtener template por ID
     * @param {string} id - ID del template
     * @returns {Promise<object>}
     */
    getTemplate: async (id) => {
        const response = await get(`${BASE_URL}/templates/${id}`);
        return response?.success ? response : { success: true, template: response };
    },

    /**
     * Obtener dimensiones estándar
     * @returns {Promise<object>}
     */
    getDimensionesEstandar: async () => {
        const response = await get(`${BASE_URL}/templates/dimensiones-estandar`);
        return response?.success ? response : { success: true, dimensiones: response?.dimensiones || [] };
    },

    /**
     * Crear infografía desde template
     * @param {string} templateId - ID del template
     * @param {string} marcaId - ID de la marca
     * @param {object} options - Opciones (nombre, descripcion, variables)
     * @returns {Promise<object>}
     */
    crearDesdeTemplate: async (templateId, marcaId, options = {}) => {
        const body = {
            templateId,
            marcaId,
            nombre: options.nombre,
            descripcion: options.descripcion,
            variables: options.variables
        };

        const response = await post(`${BASE_URL}/templates/crear-infografia`, body);
        return response?.success ? response : { success: true, infografia: response?.infografia };
    },

    /**
     * Crear template desde infografía existente
     * @param {string} infografiaId - ID de la infografía
     * @param {object} templateData - Datos del template (nombre, descripcion, categoria, etc.)
     * @returns {Promise<object>}
     */
    convertirATemplate: async (infografiaId, templateData) => {
        const response = await post(`${BASE_URL}/templates/convertir/${infografiaId}`, templateData);
        return response;
    },

    /**
     * Actualizar template
     * @param {string} id - ID del template
     * @param {object} updates - Campos a actualizar
     * @returns {Promise<object>}
     */
    updateTemplate: async (id, updates) => {
        const response = await patch(`${BASE_URL}/templates/${id}`, updates);
        return response?.success ? response : { success: true, template: response };
    },

    /**
     * Eliminar template
     * @param {string} id - ID del template
     * @returns {Promise<object>}
     */
    deleteTemplate: async (id) => {
        const response = await del(`${BASE_URL}/templates/${id}`);
        return response;
    }
};

export default infografiaService;

